<template>
  <div>
  <v-dialog v-model="dialogVisible" max-width="1200" persistent>
    <v-card>
      <v-toolbar dark color="purple">
        <v-toolbar-title>Asignar Turnos - {{ paciente ? paciente.nombrePaciente : '' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <v-alert v-if="error" type="error" outlined class="mb-4">
          {{ error }}
        </v-alert>

        <!-- Indicador de progreso del circuito -->
        <div v-if="estudios.length > 0" class="mb-4">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="body-2 font-weight-medium">
              Progreso: {{ estudiosCompletados }} / {{ totalEstudios }} estudios asignados
            </span>
            <span class="body-2 font-weight-medium">
              {{ porcentajeProgreso }}%
            </span>
          </div>
          <v-progress-linear
            :value="porcentajeProgreso"
            :color="porcentajeProgreso === 100 ? 'green' : 'purple'"
            height="10"
            rounded
          ></v-progress-linear>
        </div>

        <!-- Estudios -->
        <div v-if="estudios.length > 0">
          <v-card
            v-for="estudio in estudios"
            :key="estudio.id"
            outlined
            class="mb-3"
            :class="{ 'estudio-programado': estudio.estado !== 'PENDIENTE' }"
          >
            <v-card-title class="py-2 px-4">
              <v-chip small :color="colorEstadoEstudio(estudio.estado)" dark class="mr-3">
                {{ estudio.estado }}
              </v-chip>
              <span class="text-subtitle-1 font-weight-bold">
                {{ estudio.orden }}. {{ estudio.nombre }}
              </span>
              <v-spacer></v-spacer>
              <span v-if="estudio.estado === 'PROGRAMADO'" class="blue--text text-caption">
                <v-icon small color="blue" class="mr-1">mdi-check-circle</v-icon>
                Turno asignado
              </span>
              <span v-if="estudio.estado === 'REALIZADO'" class="green--text text-caption">
                <v-icon small color="green" class="mr-1">mdi-check-circle</v-icon>
                Realizado
              </span>
            </v-card-title>

            <!-- Info del turno para PROGRAMADO/REALIZADO -->
            <v-card-text v-if="estudio.estado !== 'PENDIENTE'" class="pt-0 px-4 pb-2">
              <div class="d-flex flex-wrap text-caption grey--text text--darken-2" style="gap: 16px;">
                <span v-if="estudio.profesionalNombre">
                  <v-icon x-small class="mr-1">mdi-account</v-icon>{{ estudio.profesionalNombre }}
                </span>
                <span v-if="estudio.fechaTurno">
                  <v-icon x-small class="mr-1">mdi-calendar</v-icon>{{ estudio.fechaTurno }}
                </span>
                <span v-if="estudio.horaTurno">
                  <v-icon x-small class="mr-1">mdi-clock</v-icon>{{ estudio.horaTurno }}
                </span>
              </div>
              <v-btn
                v-if="estudio.estado === 'PROGRAMADO'"
                small
                outlined
                color="orange"
                class="mt-2"
                @click="reprogramarEstudio(estudio)"
              >
                <v-icon left small>mdi-calendar-refresh</v-icon>
                Reprogramar
              </v-btn>
            </v-card-text>

            <v-card-text v-if="estudio.estado === 'PENDIENTE'" class="pt-0 px-4 pb-3">
              <!-- Selector de profesional -->
              <v-row dense align="center">
                <v-col cols="12" md="5">
                  <v-autocomplete
                    v-model="estudio.idProfesional"
                    :items="estudio.profesionales"
                    item-text="nombreProfesional"
                    item-value="idProfesional"
                    label="Seleccionar profesional"
                    prepend-icon="mdi-account"
                    dense
                    outlined
                    hide-details
                    :loading="estudio.cargandoProfesionales"
                    @focus="cargarProfesionales(estudio)"
                    @change="onProfesionalChange(estudio)"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <!-- Grilla de slots semanal -->
              <div v-if="estudio.idProfesional && estudio.slotsLoaded" class="mt-3">
                <!-- Navegación de semana -->
                <div class="d-flex align-center mb-2">
                  <v-btn icon small @click="semanaAnterior(estudio)" :disabled="estudio.cargandoSlots || esSemanaActual(estudio)">
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <span class="text-caption font-weight-medium mx-2">
                    {{ rangoSemana(estudio) }}
                  </span>
                  <v-btn icon small @click="semanaSiguiente(estudio)" :disabled="estudio.cargandoSlots">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                  <v-btn x-small outlined color="primary" @click="irAHoy(estudio)" class="ml-2">Hoy</v-btn>
                  <v-progress-circular
                    v-if="estudio.cargandoSlots"
                    indeterminate
                    size="16"
                    width="2"
                    color="purple"
                    class="ml-3"
                  ></v-progress-circular>
                </div>

                <!-- Leyenda -->
                <div class="mb-2">
                  <v-icon x-small color="green darken-1" class="mr-1">mdi-circle</v-icon>
                  <span class="text-caption mr-3">Disponible (click = asignar)</span>
                  <v-icon x-small color="red darken-1" class="mr-1">mdi-circle</v-icon>
                  <span class="text-caption mr-3">Ocupado (click = entre turno)</span>
                </div>

                <!-- Días de la semana con slots -->
                <v-row dense>
                  <v-col
                    v-for="dia in getDiasSemana(estudio)"
                    :key="dia.diaIdx"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                  >
                    <v-card
                      outlined
                      class="dia-card"
                      :class="{
                        'dia-pasado': dia.esPasado,
                        'dia-hoy': esHoy(dia.fecha)
                      }"
                    >
                      <div class="dia-header pa-1 px-2">
                        <span class="text-caption font-weight-bold">{{ dia.nombreCorto }}</span>
                        <span class="text-caption ml-1 grey--text">{{ dia.fechaStr }}</span>
                      </div>
                      <v-divider></v-divider>
                      <div class="pa-1 chips-container" v-if="!dia.esPasado">
                        <v-chip
                          v-for="(slot, idx) in getSlotsDelDia(estudio, dia.diaIdx)"
                          :key="idx"
                          :color="getSlotColor(slot)"
                          :text-color="getSlotTextColor(slot)"
                          small
                          label
                          class="chip-slot mr-1 mb-1"
                          :disabled="!!slot.idExcepcion || estudio.asignando"
                          @click="clickSlot(estudio, slot)"
                        >
                          {{ slot.hora ? slot.hora.substring(0, 5) : '' }}
                        </v-chip>
                        <span
                          v-if="getSlotsDelDia(estudio, dia.diaIdx).length === 0"
                          class="text-caption grey--text pa-1"
                        >
                          Sin agenda
                        </span>
                      </div>
                      <div v-else class="pa-2 text-center">
                        <span class="text-caption grey--text">—</span>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <!-- Loading slots -->
              <div v-if="estudio.idProfesional && estudio.cargandoSlots && !estudio.slotsLoaded" class="text-center pa-3">
                <v-progress-circular indeterminate size="24" color="purple"></v-progress-circular>
                <span class="text-caption ml-2">Cargando turnos disponibles...</span>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <v-alert v-if="!cargando && estudios.length === 0 && !error" type="info" outlined>
          No se encontraron estudios para este circuito.
        </v-alert>

        <div v-if="cargando" class="text-center pa-4">
          <v-progress-circular indeterminate color="purple"></v-progress-circular>
          <p class="mt-2">Cargando estudios del circuito...</p>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cerrar">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Dialog para seleccionar Obra Social antes de confirmar -->
  <v-dialog v-model="showObraSocialDialog" max-width="450" persistent>
    <v-card>
      <v-toolbar dark color="purple" dense>
        <v-toolbar-title class="text-subtitle-1">Seleccionar Obra Social</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancelarObraSocial">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-4">
        <p class="text-body-2 mb-3">
          Turno: <strong>{{ slotPendiente ? slotPendiente.fechaTurnoString + ' ' + (slotPendiente.hora || '').substring(0, 5) : '' }}</strong>
        </p>
        <v-autocomplete
          v-model="obraSocialSeleccionada"
          :items="obrasSociales"
          item-text="nombre"
          item-value="idObraSocial"
          label="Obra Social *"
          prepend-icon="mdi-shield-cross"
          dense
          outlined
          :loading="cargandoOS"
          no-data-text="No hay obras sociales configuradas"
        ></v-autocomplete>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="error" text @click="cancelarObraSocial">Cancelar</v-btn>
        <v-btn color="success" :disabled="!obraSocialSeleccionada" :loading="estudioPendiente && estudioPendiente.asignando" @click="confirmarAsignacion">
          <v-icon left small>mdi-check</v-icon>
          Confirmar Turno
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import axios from "../../axios-auth.js";

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    paciente: {
      type: Object,
      default: null,
    },
    idCircuito: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      estudios: [],
      cargando: false,
      error: null,
      // Obra social dialog
      showObraSocialDialog: false,
      obrasSociales: [],
      obraSocialSeleccionada: null,
      cargandoOS: false,
      estudioPendiente: null,
      slotPendiente: null,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        if (!val) this.cerrar();
      },
    },
    totalEstudios() {
      return this.estudios.length;
    },
    estudiosCompletados() {
      return this.estudios.filter((e) => e.estado !== "PENDIENTE").length;
    },
    porcentajeProgreso() {
      if (this.totalEstudios === 0) return 0;
      return Math.round((this.estudiosCompletados / this.totalEstudios) * 100);
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.cargarEstudiosCircuito();
      }
    },
  },
  mounted() {
    if (this.visible) {
      this.cargarEstudiosCircuito();
    }
  },
  methods: {
    cargarEstudiosCircuito() {
      if (!this.idCircuito) {
        this.error = "No se pudo determinar el circuito.";
        return;
      }
      if (!this.paciente || !this.paciente.idCircuitoPaciente) {
        this.error = "No se pudo determinar el paciente del circuito.";
        return;
      }
      this.cargando = true;
      this.error = null;
      this.estudios = [];

      let self = this;
      axios
        .get("/circuitos/" + this.idCircuito + "/estudios-estado/" + this.paciente.idCircuitoPaciente)
        .then((response) => {
          self.cargando = false;
          if (response.data && Array.isArray(response.data)) {
            self.estudios = response.data
              .sort((a, b) => a.orden - b.orden)
              .map((e) => ({
                id: e.id,
                idTipoTurno: e.idTipoTurno,
                nombreTipoTurno: e.nombreTipoTurno || "",
                orden: e.orden,
                nombre: e.nombre,
                duracionMinutos: e.duracionMinutos || 0,
                estado: e.estado || "PENDIENTE",
                profesionalNombre: e.profesionalNombre || null,
                fechaTurno: e.fechaTurno || null,
                horaTurno: e.horaTurno || null,
                idProfesional: null,
                profesionales: [],
                cargandoProfesionales: false,
                profesionalesCargados: false,
                // Slot picker data
                fechaInicioSemana: null,
                slotsData: [[], [], [], [], [], [], []],
                slotsLoaded: false,
                cargandoSlots: false,
                asignando: false,
              }));
          }
        })
        .catch(() => {
          self.cargando = false;
          self.error = "Error al cargar los estudios del circuito.";
        });
    },

    cargarProfesionales(estudio) {
      if (estudio.profesionalesCargados || estudio.cargandoProfesionales) {
        return;
      }
      estudio.cargandoProfesionales = true;
      let self = this;
      axios
        .get("/circuitos/estudios/" + estudio.id + "/profesionales")
        .then((response) => {
          estudio.cargandoProfesionales = false;
          estudio.profesionalesCargados = true;
          if (response.data && Array.isArray(response.data)) {
            let profesionales = response.data;
            // Filtrar por profesionales del usuario si tiene restricción
            const profUsuario = self.$store.state.usuario.profesionales;
            if (profUsuario && profUsuario.length > 0) {
              const idsProfUsuario = profUsuario.map(p => p.idProfesional || p.Idprofesional || p.id);
              profesionales = profesionales.filter(p => idsProfUsuario.includes(p.idProfesional));
            }
            profesionales.sort((a, b) => a.nombreProfesional.localeCompare(b.nombreProfesional));
            self.$set(estudio, "profesionales", profesionales);
          }
        })
        .catch(() => {
          estudio.cargandoProfesionales = false;
        });
    },

    onProfesionalChange(estudio) {
      if (!estudio.idProfesional) {
        this.$set(estudio, "slotsLoaded", false);
        this.$set(estudio, "slotsData", [[], [], [], [], [], [], []]);
        return;
      }
      // Initialize week to current week
      this.$set(estudio, "fechaInicioSemana", this.calcularInicioSemana(new Date()));
      this.cargarSlots(estudio);
    },

    calcularInicioSemana(fecha) {
      const d = new Date(fecha);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff);
      d.setHours(0, 0, 0, 0);
      return d;
    },

    cargarSlots(estudio) {
      if (!estudio.idProfesional || !estudio.fechaInicioSemana) return;

      this.$set(estudio, "cargandoSlots", true);

      const fecha = this.toISOLocal(estudio.fechaInicioSemana);
      const idInstitucion = this.$store.state.usuario.institucion.idInstitucion;

      let self = this;
      axios
        .get("/Turnos/ArmarTurnosWideOptimizado", {
          params: {
            idProfesional: estudio.idProfesional,
            fecha: fecha,
            idInstitucion: idInstitucion,
          },
        })
        .then((response) => {
          self.$set(estudio, "cargandoSlots", false);
          self.$set(estudio, "slotsLoaded", true);
          if (response.data && Array.isArray(response.data)) {
            self.$set(estudio, "slotsData", response.data);
          } else {
            self.$set(estudio, "slotsData", [[], [], [], [], [], [], []]);
          }
        })
        .catch(() => {
          self.$set(estudio, "cargandoSlots", false);
          self.$set(estudio, "slotsLoaded", true);
          self.$set(estudio, "slotsData", [[], [], [], [], [], [], []]);
        });
    },

    semanaAnterior(estudio) {
      const d = new Date(estudio.fechaInicioSemana);
      d.setDate(d.getDate() - 7);
      this.$set(estudio, "fechaInicioSemana", d);
      this.cargarSlots(estudio);
    },

    semanaSiguiente(estudio) {
      const d = new Date(estudio.fechaInicioSemana);
      d.setDate(d.getDate() + 7);
      this.$set(estudio, "fechaInicioSemana", d);
      this.cargarSlots(estudio);
    },

    irAHoy(estudio) {
      this.$set(estudio, "fechaInicioSemana", this.calcularInicioSemana(new Date()));
      this.cargarSlots(estudio);
    },

    esSemanaActual(estudio) {
      if (!estudio.fechaInicioSemana) return true;
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const inicioSemanaHoy = this.calcularInicioSemana(hoy);
      return estudio.fechaInicioSemana.getTime() <= inicioSemanaHoy.getTime();
    },

    getDiasSemana(estudio) {
      if (!estudio.fechaInicioSemana) return [];
      const dias = [];
      const nombresCortos = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      for (let i = 0; i < 7; i++) {
        const d = new Date(estudio.fechaInicioSemana);
        d.setDate(d.getDate() + i);
        dias.push({
          nombreCorto: nombresCortos[i],
          fechaStr: d.getDate() + "/" + (d.getMonth() + 1),
          fecha: new Date(d),
          diaIdx: i,
          esPasado: d < hoy,
        });
      }
      return dias;
    },

    getSlotsDelDia(estudio, diaIdx) {
      if (!estudio.slotsData || !estudio.slotsData[diaIdx]) return [];
      return estudio.slotsData[diaIdx];
    },

    esSlotDisponible(slot) {
      return slot.paciente === "Disponible" && !slot.idTurno;
    },

    getSlotColor(slot) {
      if (slot.idExcepcion) return "grey lighten-2";
      if (!slot.idTurno && slot.paciente === "Disponible") return "green lighten-4";
      return "red lighten-4";
    },

    getSlotTextColor(slot) {
      if (slot.idExcepcion) return "grey darken-1";
      if (!slot.idTurno && slot.paciente === "Disponible") return "green darken-3";
      return "red darken-3";
    },

    rangoSemana(estudio) {
      if (!estudio.fechaInicioSemana) return "";
      const inicio = new Date(estudio.fechaInicioSemana);
      const fin = new Date(estudio.fechaInicioSemana);
      fin.setDate(fin.getDate() + 6);
      return (
        inicio.getDate() + "/" + (inicio.getMonth() + 1) +
        " - " +
        fin.getDate() + "/" + (fin.getMonth() + 1) + "/" + fin.getFullYear()
      );
    },

    esHoy(fecha) {
      const hoy = new Date();
      return (
        fecha.getDate() === hoy.getDate() &&
        fecha.getMonth() === hoy.getMonth() &&
        fecha.getFullYear() === hoy.getFullYear()
      );
    },

    clickSlot(estudio, slot) {
      if (slot.idExcepcion) return;
      if (estudio.asignando) return;

      if (this.esSlotDisponible(slot)) {
        // Slot disponible: asignar normalmente
        this.asignarSlot(estudio, slot);
      } else {
        // Slot ocupado: asignar como entre-turno (1 minuto menos de hora)
        const hora = slot.hora ? slot.hora.substring(0, 5) : "";
        const parts = hora.split(":");
        let h = parseInt(parts[0]);
        let m = parseInt(parts[1]) - 1;
        if (m < 0) { m = 59; h--; }
        const horaET = String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");

        // Crear un slot modificado para entre-turno
        const slotET = {
          ...slot,
          hora: horaET + ":00",
          entreTurno: 1,
        };
        this.asignarSlot(estudio, slotET);
      }
    },

    asignarSlot(estudio, slot) {
      if (!this.paciente || !this.paciente.idCircuitoPaciente) return;
      if (estudio.asignando) return;

      // Guardar referencia al estudio y slot pendientes
      this.estudioPendiente = estudio;
      this.slotPendiente = slot;
      this.obraSocialSeleccionada = null;

      // Cargar obras sociales si no están cargadas
      if (this.obrasSociales.length === 0) {
        this.cargarObrasSociales();
      }

      // Abrir dialog de obra social
      this.showObraSocialDialog = true;
    },

    cargarObrasSociales() {
      this.cargandoOS = true;
      const idInstitucion = this.$store.state.usuario.institucion.idInstitucion;
      let self = this;
      axios
        .get("/ObraSocial/GetObraSocialActivasDeInst", {
          params: { idInstitucion: idInstitucion, idProfesional: 0 },
        })
        .then((response) => {
          self.cargandoOS = false;
          self.obrasSociales = response.data || [];
          // Preseleccionar la primera si hay una sola o la default
          if (self.obrasSociales.length === 1) {
            self.obraSocialSeleccionada = self.obrasSociales[0].idObraSocial;
          }
        })
        .catch(() => {
          self.cargandoOS = false;
          self.obrasSociales = [];
        });
    },

    cancelarObraSocial() {
      this.showObraSocialDialog = false;
      this.estudioPendiente = null;
      this.slotPendiente = null;
      this.obraSocialSeleccionada = null;
    },

    confirmarAsignacion() {
      if (!this.obraSocialSeleccionada) return;
      if (!this.estudioPendiente || !this.slotPendiente) return;

      const estudio = this.estudioPendiente;
      const slot = this.slotPendiente;

      this.$set(estudio, "asignando", true);

      const fechaTurnoString = slot.fechaTurnoString;
      const horaInicioString = slot.hora ? slot.hora.substring(0, 5) : "";
      const duracionTurno = slot.tiempoTurno || estudio.duracionMinutos || 15;
      const entreTurno = slot.entreTurno || 0;

      let payload = {
        idCircuitoPaciente: this.paciente.idCircuitoPaciente,
        idCircuitoEstudio: estudio.id,
        idProfesional: estudio.idProfesional,
        fechaTurnoString: fechaTurnoString,
        horaInicioString: horaInicioString,
        duracionTurno: duracionTurno,
        entreTurno: entreTurno,
        idObraSocial: this.obraSocialSeleccionada,
      };

      let self = this;
      axios
        .post("/circuitos/asignar-turno-slot", payload)
        .then(() => {
          self.$set(estudio, "asignando", false);
          self.$set(estudio, "estado", "PROGRAMADO");
          // Actualizar datos visuales del turno
          var profSeleccionado = estudio.profesionales.find(p => p.idProfesional === estudio.idProfesional);
          self.$set(estudio, "profesionalNombre", profSeleccionado ? profSeleccionado.nombreProfesional : "");
          self.$set(estudio, "fechaTurno", fechaTurnoString);
          self.$set(estudio, "horaTurno", horaInicioString);
          self.showObraSocialDialog = false;
          self.estudioPendiente = null;
          self.slotPendiente = null;
          self.showAlert({
            vm: self,
            icon: "success",
            title: "Turno asignado correctamente",
          });
        })
        .catch((err) => {
          self.$set(estudio, "asignando", false);
          const msg = err.response && err.response.data
            ? err.response.data
            : "Error al asignar el turno";
          self.showAlert({
            vm: self,
            icon: "error",
            title: msg,
          });
        });
    },

    colorEstadoEstudio(estado) {
      switch (estado) {
        case "PENDIENTE":
          return "orange";
        case "PROGRAMADO":
          return "blue";
        case "REALIZADO":
          return "green";
        default:
          return "grey";
      }
    },

    reprogramarEstudio(estudio) {
      this.$set(estudio, "estado", "PENDIENTE");
      this.$set(estudio, "slotsLoaded", false);
      this.$set(estudio, "slotsData", [[], [], [], [], [], [], []]);
      this.$set(estudio, "idProfesional", null);
    },

    showAlert(alertInfo) {
      alertInfo.vm.$swal.fire({
        icon: alertInfo.icon,
        title: alertInfo.title,
        showConfirmButton: false,
        background: "#ececec",
        timer: 2500,
      });
    },

    toISOLocal(d) {
      const z = (n) => ("0" + n).slice(-2);
      return d.getFullYear() + "-" + z(d.getMonth() + 1) + "-" + z(d.getDate());
    },

    cerrar() {
      this.$emit("cerrar");
    },
  },
};
</script>

<style scoped>
.estudio-programado {
  opacity: 0.7;
  background-color: #f5f5f5;
}

.dia-card {
  transition: all 0.2s;
  min-height: 60px;
}

.dia-pasado {
  opacity: 0.35;
}

.dia-hoy {
  border: 2px solid #1976d2 !important;
}

.dia-header {
  background: #fafafa;
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  min-height: 32px;
}

.chip-slot {
  cursor: pointer;
  font-weight: 600;
  font-size: 0.7rem !important;
  min-width: 46px;
  justify-content: center;
}

.chip-slot:hover:not(.v-chip--disabled) {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}
</style>
